/**
 * Who tagged us — the section under Channel stats.
 *
 * A grid of thumbnails rather than a list of text, per the design's reasoning:
 * "so a post can be judged at a glance, without opening Instagram." A tag is a
 * picture and a number of likes; describing it in words made you open Instagram
 * to find out whether it mattered.
 *
 * The one action writes the handle onto the record you pick — it lives on the
 * ambassador, partner, event or lead, which is the one place it belongs.
 * Nothing is written to the mention itself; that collection is collector-owned,
 * and attribution is resolved here against the records as they are now, so a
 * linked card updates the moment you close the picker rather than at the next
 * nightly run.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { nf } from '@/shared/util/numbers'
import { subscribeToMentions } from './repository/mentionsRepository'
import { MATCH_LABEL, type Mention, type MentionMatchKind } from '@/types/mentions'
import { formatDate } from '@/shared/util/dates'
import { LinkHandleModal } from './LinkHandleModal'
import { loadHandleIndex, type HandleTarget } from './repository/handleTargets'
import './Mentions.css'

interface Resolved extends Mention {
  liveKind: MentionMatchKind | null
  liveName: string
}

type Filter = MentionMatchKind | 'all' | 'none'

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'ambassador', label: 'Ambassadors' },
  { key: 'partner', label: 'Partners' },
  { key: 'lead', label: 'Leads' },
  { key: 'event', label: 'Events' },
  { key: 'none', label: 'Not recognised' },
]


export function Mentions() {
  const [rows, setRows] = useState<Mention[]>([])
  const [index, setIndex] = useState<Map<string, HandleTarget>>(new Map())
  const [filter, setFilter] = useState<Filter>('all')
  const [linking, setLinking] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(
    () =>
      subscribeToMentions(
        (m) => {
          setRows(m)
          setLoading(false)
        },
        (e) => {
          setError(e.message)
          setLoading(false)
        },
      ),
    [],
  )

  const refreshIndex = useCallback(() => {
    loadHandleIndex()
      .then(setIndex)
      .catch(() => setIndex(new Map()))
  }, [])
  useEffect(refreshIndex, [refreshIndex])

  const resolved: Resolved[] = useMemo(
    () =>
      rows.map((r) => {
        const hit = index.get(r.username)
        return {
          ...r,
          // The live record wins over the collector's stored match, so a handle
          // moved from one record to another follows.
          liveKind: hit?.kind ?? r.matchKind,
          liveName: hit?.name ?? r.matchName,
        }
      }),
    [rows, index],
  )

  const shown = useMemo(
    () =>
      resolved.filter((m) =>
        filter === 'all' ? true : filter === 'none' ? m.liveKind === null : m.liveKind === filter,
      ),
    [resolved, filter],
  )

  const kpis = useMemo(() => {
    const unknown = resolved.filter((m) => m.liveKind === null).length
    // Summed only where a count exists, so a missing one does not read as a
    // zero and drag the total down.
    const likes = resolved.reduce((n, m) => n + (m.likeCount ?? 0), 0)
    return [
      { value: nf(resolved.length), label: resolved.length === 1 ? 'tag' : 'tags' },
      { value: nf(resolved.length - unknown), label: 'attributed' },
      { value: nf(likes), label: 'likes across them' },
    ]
  }, [resolved])

  if (!loading && rows.length === 0 && !error) return null

  return (
    <section className="mentions">
      <div className="mentions__head">
        <h3 className="mentions__title">Who tagged us</h3>
        <div className="mentions__kpis">
          {kpis.map((k) => (
            <div className="mentions__kpi" key={k.label}>
              <strong>{k.value}</strong> {k.label}
            </div>
          ))}
        </div>
      </div>

      <p className="mentions__sub">
        Instagram posts tagging @skida.app, matched against ambassador, partner, lead and event
        handles. Matching is on the exact handle — nothing is guessed. Posts from private accounts
        never appear here; Meta does not return them, so a quiet ambassador may not be quiet.
      </p>

      {error && <div className="empty-note">Could not read mentions: {error}</div>}

      <div className="mentions__filters">
        {FILTERS.map((f) => {
          const n =
            f.key === 'all'
              ? resolved.length
              : f.key === 'none'
                ? resolved.filter((m) => m.liveKind === null).length
                : resolved.filter((m) => m.liveKind === f.key).length
          // An empty category is noise; "All" stays whatever happens.
          if (n === 0 && f.key !== 'all') return null
          return (
            <button
              key={f.key}
              className="chip"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label} ({n})
            </button>
          )
        })}
      </div>

      <div className="mentions__grid">
        {shown.map((m) => (
          <Card m={m} key={m.id} onLink={setLinking} />
        ))}
        {shown.length === 0 && <div className="empty-note">No mention under that filter. Widen it to see who else has tagged us.</div>}
      </div>

      {linking && (
        <LinkHandleModal
          handle={linking}
          onClose={() => setLinking(null)}
          onLinked={() => {
            setLinking(null)
            refreshIndex()
          }}
        />
      )}
    </section>
  )
}

function Card({ m, onLink }: { m: Resolved; onLink: (handle: string) => void }) {
  // Instagram's CDN links are signed and expire within days. A dead one has to
  // leave a card that still reads, not a broken frame.
  const [imageOk, setImageOk] = useState(true)

  return (
    <div className={`mcard${m.liveKind ? '' : ' mcard--unknown'}`}>
      <a
        className="mcard__media"
        href={m.permalink || `https://instagram.com/${m.username}`}
        target="_blank"
        rel="noreferrer"
        title={m.caption || undefined}
      >
        {m.thumbnailUrl && imageOk ? (
          <img
            className="mcard__img"
            src={m.thumbnailUrl}
            alt=""
            loading="lazy"
            onError={() => setImageOk(false)}
          />
        ) : (
          <div className="mcard__noimg">{m.productType.toLowerCase() || 'post'}</div>
        )}
        {m.productType === 'REELS' && <span className="mcard__kind">▶ reel</span>}
      </a>

      <div className="mcard__body">
        <div className="mcard__handle">@{m.username}</div>

        {m.liveKind ? (
          <div className="mcard__match">
            {MATCH_LABEL[m.liveKind]} · {m.liveName}
          </div>
        ) : (
          <button className="mcard__link" onClick={() => onLink(m.username)}>
            Link to a record…
          </button>
        )}

        <div className="mcard__stats">
          {m.likeCount !== null && <span>♥ {nf(m.likeCount)}</span>}
          {m.commentCount !== null && <span>💬 {nf(m.commentCount)}</span>}
          <span className="mcard__date">{m.postedAt ? formatDate(m.postedAt) : ''}</span>
        </div>

        {/* Two records claiming one handle. Shown rather than resolved: picking
            a winner silently would make the other permanently invisible. */}
        {m.conflict && <div className="mcard__conflict">{m.conflict}</div>}
      </div>
    </div>
  )
}
